import { useState } from 'react';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type SignupCheckboxKey = 'updates' | 'demo' | 'event' | 'report';

export type ApolloSubscribeRequestBody = {
  email: string;
  mailingList: Record<SignupCheckboxKey, boolean>;
};

export type ApolloSubscribeResponse = {
  success: boolean;
  email: string;
  message?: string;
};

export const defaultCheckboxes: Record<SignupCheckboxKey, boolean> = {
  updates: false,
  demo: false,
  event: false,
  report: false,
};

export const useSignupForm = () => {
  const [isFocused, setIsFocused] = useState(false);
  const [email, setEmail] = useState('');
  const [checkboxes, setCheckboxes] =
    useState<Record<SignupCheckboxKey, boolean>>(defaultCheckboxes);
  const [isSent, setIsSent] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your email');
      return;
    }

    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!Object.values(checkboxes).some(Boolean)) {
      setError('Please select at least one checkbox');
      return;
    }

    setIsPending(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_URL;
      const body: ApolloSubscribeRequestBody = {
        email,
        mailingList: checkboxes,
      };

      const response = await fetch(`${apiUrl}/apollo/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = (await response.json()) as ApolloSubscribeResponse & {
        error?: string;
      };

      if (!response.ok) {
        setError(data.error || 'Failed to subscribe');
        setIsPending(false);
        return;
      }

      console.log('[SignupForm] Successfully subscribed:', data);
      setIsSent(true);
      setIsPending(false);
    } catch (err) {
      console.error('[SignupForm] Subscription error:', err);
      setError('Something went wrong. Please try again.');
      setIsPending(false);
    }
  };

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsFocused(false);
    setEmail('');
    setCheckboxes(defaultCheckboxes);
    setIsSent(false);
    setError(null);
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSent || isPending) return;
    const name = e.target.name as SignupCheckboxKey;
    if (!(name in defaultCheckboxes)) return;
    const checked = e.target.checked;
    setCheckboxes(prev => ({ ...prev, [name]: checked }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isSent) {
      setEmail(e.target.value);
      setError(null);
    }
  };

  const handleFocus = () => {
    if (!isSent) {
      setIsFocused(true);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const displayEmail = isSent ? 'SENT!' : email;

  return {
    isFocused,
    isSent,
    error,
    email: displayEmail,
    checkboxes,
    isPending,
    handleSubmit,
    handleChange,
    handleCheckboxChange,
    handleFocus,
    handleBlur,
    handleReset,
  };
};
